import { useAppDispatch } from "@redux/store";
import {
  createModelScreenerFilterSettingThunk,
  deleteModelScreenerFilterSettingThunk,
  exportModelScreenerExcelThunk,
  getModelScreenerDataByDateRangeThunk,
  getModelScreenerDataThunk,
  loadModelScreenerFilterSettingByIdThunk,
  loadModelScreenerFilterSettingsThunk,
  putModelScreenerFavoriteModelThunk,
  updateModelScreenerFilterSettingThunk,
  setModelScreenerFilterSettingAsDefaultThunk,
  loadModelScreenerColumnSettingsThunk,
  createMSColumnSettingThunk,
  updateMSColumnSettingThunk,
  loadMSColumnSettingByIdThunk,
  deleteModelScreenerColumnSettingThunk,
  setMSColumnSettingAsDefaultThunk,
} from "@redux/slices/model-screener";
import { APIResponse } from "@models/core";
import { useNotification } from "@hooks/useNotification";
import { ModelScreenerExportModelsDto } from "@models/model-screener/entities/modelScreenerData";
import {
  ModelScreenerColumnSettingDTO,
  ModelScreenerFilterDTO,
} from "@models/model-screener/entities/modelScreenerFilter";

export const useModelScreener = () => {
  const dispatch = useAppDispatch();
  const { notify } = useNotification();

  const handleApiResponse = <T>({
    response,
    showSuccessMessage = false,
  }: {
    response: APIResponse<T>;
    showSuccessMessage?: boolean;
  }): T | undefined => {
    if (!response.isSuccess || !response.data) {
      !response.isCancel &&
        notify(response.message, {
          variant: "error",
          close: true,
        });

      return;
    }

    showSuccessMessage &&
      notify(response.message, {
        variant: "success",
        close: true,
      });

    return response.data;
  };

  const loadModelScreenerData = async ({
    signal,
    isReloadDataByDateRange = false,
  }: {
    signal?: AbortSignal;
    isReloadDataByDateRange?: boolean;
  }) => {
    const response = await dispatch(
      getModelScreenerDataThunk({ signal, isReloadDataByDateRange }),
    ).unwrap();
    return handleApiResponse({ response });
  };

  const loadModelScreenerDataByDateRange = async ({
    signal,
    fromDate,
    toDate,
  }: {
    signal?: AbortSignal;
    fromDate: Date | null;
    toDate: Date | null;
  }) => {
    const response = await dispatch(
      getModelScreenerDataByDateRangeThunk({ signal, fromDate, toDate }),
    ).unwrap();
    return handleApiResponse({ response });
  };

  const putModelScreenerFavoriteModels = async (modelIds: string[]) => {
    const response = await dispatch(
      putModelScreenerFavoriteModelThunk(modelIds),
    ).unwrap();
    return handleApiResponse({ response });
  };

  const exportModelScreenerExcel = async (
    exportDto: ModelScreenerExportModelsDto,
  ) => {
    const response = await dispatch(
      exportModelScreenerExcelThunk(exportDto),
    ).unwrap();
    return handleApiResponse({ response });
  };

  // const loadModelScreenerUserFilters = async ({
  //   signal,
  // }: {
  //   signal?: AbortSignal;
  // }) => {
  //   const response = await dispatch(
  //     getModelScreenerUserFiltersThunk({ signal }),
  //   ).unwrap();
  //   return handleApiResponse({ response });
  // };

  const loadModelScreenerFilterSettings = async () => {
    const response = await dispatch(
      loadModelScreenerFilterSettingsThunk(),
    ).unwrap();

    return handleApiResponse({ response });
  };

  const loadModelScreenerFilterSettingById = async ({
    selectedFilterSettingId,
    signal,
  }: {
    selectedFilterSettingId: string;
    signal?: AbortSignal;
  }) => {
    const response = await dispatch(
      loadModelScreenerFilterSettingByIdThunk({
        selectedFilterSettingId,
        signal,
      }),
    ).unwrap();

    if (!response) return;

    return handleApiResponse({ response });
  };

  const deleteModelScreenerFilterSetting = async (filterSettingId: string) => {
    const response = await dispatch(
      deleteModelScreenerFilterSettingThunk(filterSettingId),
    ).unwrap();

    if (!response) return;

    return handleApiResponse({ response, showSuccessMessage: true });
  };

  const setModelScreenerFilterSettingAsDefault = async (
    filterSettingId: string,
  ) => {
    const response = await dispatch(
      setModelScreenerFilterSettingAsDefaultThunk(filterSettingId),
    ).unwrap();

    return handleApiResponse({ response });
  };

  const createModelScreenerFilterSetting = async (
    filterSetting: ModelScreenerFilterDTO,
    signal?: AbortSignal,
  ) => {
    const response = await dispatch(
      createModelScreenerFilterSettingThunk({ filterSetting, signal }),
    ).unwrap();

    if (!response) return;

    return handleApiResponse({ response, showSuccessMessage: true });
  };

  const updateModelScreenerFilterSetting = async (
    filterSetting: ModelScreenerFilterDTO,
    signal?: AbortSignal,
  ) => {
    const response = await dispatch(
      updateModelScreenerFilterSettingThunk({ filterSetting, signal }),
    ).unwrap();

    if (!response) return;

    return handleApiResponse({ response, showSuccessMessage: true });
  };

  const loadModelScreenerColumnSettings = async () => {
    const response = await dispatch(
      loadModelScreenerColumnSettingsThunk(),
    ).unwrap();

    return handleApiResponse({ response });
  };

  const createMSColumnSetting = async (
    columnSetting: ModelScreenerColumnSettingDTO,
    signal?: AbortSignal,
  ) => {
    const response = await dispatch(
      createMSColumnSettingThunk({
        columnSetting: columnSetting,
        signal,
      }),
    ).unwrap();

    if (!response) return;

    return handleApiResponse({ response, showSuccessMessage: true });
  };

  const updateMSColumnSetting = async (
    columnSetting: ModelScreenerColumnSettingDTO,
    signal?: AbortSignal,
  ) => {
    const response = await dispatch(
      updateMSColumnSettingThunk({ columnSetting, signal }),
    ).unwrap();

    if (!response) return;

    return handleApiResponse({ response, showSuccessMessage: true });
  };

  const loadMSColumnSettingById = async ({
    selectedColumnSettingId,
    signal,
  }: {
    selectedColumnSettingId: string;
    signal?: AbortSignal;
  }) => {
    const response = await dispatch(
      loadMSColumnSettingByIdThunk({
        selectedColumnSettingId,
        signal,
      }),
    ).unwrap();

    if (!response) return;

    return handleApiResponse({ response });
  };

  const deleteModelScreenerColumnSetting = async (columnSettingId: string) => {
    const response = await dispatch(
      deleteModelScreenerColumnSettingThunk(columnSettingId),
    ).unwrap();

    if (!response) return;

    return handleApiResponse({ response, showSuccessMessage: true });
  };

  const setMSColumnSettingAsDefault = async (columnSettingId: string) => {
    const response = await dispatch(
      setMSColumnSettingAsDefaultThunk(columnSettingId),
    ).unwrap();

    return handleApiResponse({ response });
  };

  return {
    loadModelScreenerData,
    loadModelScreenerDataByDateRange,
    putModelScreenerFavoriteModels,
    exportModelScreenerExcel,
    loadModelScreenerFilterSettings,
    loadModelScreenerFilterSettingById,
    deleteModelScreenerFilterSetting,
    setModelScreenerFilterSettingAsDefault,
    createModelScreenerFilterSetting,
    updateModelScreenerFilterSetting,
    loadModelScreenerColumnSettings,
    createMSColumnSetting,
    updateMSColumnSetting,
    loadMSColumnSettingById,
    deleteModelScreenerColumnSetting,
    setMSColumnSettingAsDefault,
    //loadModelScreenerUserFilters,
  };
};
